import React from 'react';
import { Form, Button, Dropdown, Spinner } from 'react-bootstrap';
import { Upload, Stars, ChevronDown, PlusCircle, InfoCircle, ArrowReturnLeft } from 'react-bootstrap-icons';
import { Project } from '../../types/project.types';
import { useProjects } from '../../contexts/ProjectContext';
import FileCard from '../FileCard';
import './ChatContainer.css';

interface ChatContainerProps {
  inputText: string;
  onInputChange: (text: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onUpload?: () => void;
  selectedProjectId: string | null;
  onProjectChange: (projectId: string | null) => void;
  onProjectCreated?: (project: Project) => void;
  onLimitError?: () => void;
  projects: Project[];
  placeholder?: string;
  submitButtonText?: string;
  showUploadButton?: boolean;
  uploadedFiles?: File[];
  onRemoveFile?: (index: number) => void;
  fileError?: string | null;
  ghostText?: string;
  isLoadingGhostText?: boolean;
  onGhostTextAccept?: () => void;
}

const ChatContainer: React.FC<ChatContainerProps> = ({
  inputText,
  onInputChange,
  onSubmit,
  onUpload,
  selectedProjectId,
  onProjectChange,
  onProjectCreated,
  onLimitError,
  projects,
  placeholder = "HOW CAN I HELP YOU TODAY?",
  submitButtonText = "Ask Revealer",
  showUploadButton = true,
  uploadedFiles = [],
  onRemoveFile,
  fileError,
  ghostText,
  isLoadingGhostText = false,
  onGhostTextAccept,
}) => {
  const { openNewProjectDialog } = useProjects();

  const acceptGhostText = () => {
    if (!ghostText) return;
    onInputChange(ghostText);
    onGhostTextAccept?.();
  };

  return (
    <div className="chat-container">
      <Form onSubmit={onSubmit}>
        <Form.Group>
          {uploadedFiles.length > 0 && onRemoveFile && (
            <div className="files-container mb-3">
              {uploadedFiles.map((file, index) => (
                <FileCard
                  key={`${file.name}-${index}`}
                  file={file}
                  onRemove={() => onRemoveFile(index)}
                />
              ))}
            </div>
          )}

          {fileError && (
            <div className="file-error-box">
              <InfoCircle size={16} className="file-error-icon" />
              <span className="file-error-text">{fileError}</span>
            </div>
          )}

          <div
            className="textarea-wrapper position-relative"
            onDragOver={(e) => {
              if (e.dataTransfer.types.includes('application/x-signal')) {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'copy';
              }
            }}
            onDrop={(e) => {
              const signalText = e.dataTransfer.getData('text/plain');
              if (signalText) {
                e.preventDefault();
                onInputChange(inputText ? `${inputText} ${signalText}` : signalText);
              }
            }}
          >
            <Dropdown className="project-dropdown">
              <Dropdown.Toggle variant="outline-secondary" size="sm" id="project-dropdown">
                <span className="me-2">
                  {selectedProjectId
                    ? projects.find(p => p.id === selectedProjectId)?.name || 'NO PROJECT SELECTED'
                    : 'NO PROJECT SELECTED'
                  }
                </span>
                <ChevronDown />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => onProjectChange(null)}>
                  NO PROJECT SELECTED
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item
                  onClick={() => openNewProjectDialog({ onLimitError, onCreated: onProjectCreated })}
                  className="text-primary"
                >
                  <PlusCircle className="me-2" />
                  CREATE NEW PROJECT
                </Dropdown.Item>
                {projects.length > 0 && <Dropdown.Divider />}
                {projects.map(project => (
                  <Dropdown.Item
                    key={project.id}
                    onClick={() => onProjectChange(project.id)}
                    active={selectedProjectId === project.id}
                  >
                    {project.name}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>

            {isLoadingGhostText && !ghostText && !inputText && (
              <div
                className="ghost-text-overlay ghost-text-overlay--loading"
                role="status"
                aria-live="polite"
              >
                <Spinner animation="border" size="sm" />
                <span>Generating suggestion…</span>
              </div>
            )}

            {ghostText && !inputText && (
              <div className="ghost-text-overlay">{ghostText}</div>
            )}

            <Form.Control
              as="textarea"
              placeholder={!ghostText && !isLoadingGhostText ? placeholder : undefined}
              value={inputText}
              onChange={(e) => onInputChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Tab' && ghostText && !inputText) {
                  e.preventDefault();
                  acceptGhostText();
                }
              }}
              style={
                (ghostText || isLoadingGhostText) && !inputText
                  ? { background: 'transparent', position: 'relative', zIndex: 1 }
                  : undefined
              }
            />

            {ghostText && !inputText && (
              <button
                type="button"
                className="ghost-text-hint"
                onClick={acceptGhostText}
                aria-label="Accept suggested prompt"
              >
                <span className="ghost-text-hint-label">Tab to accept</span>
                <ArrowReturnLeft size={12} />
              </button>
            )}
          </div>
        </Form.Group>
        <div className="text-end mt-3 chat-action-container">
          {showUploadButton && (
            <Button
              type="button"
              variant="link"
              size="lg"
              onClick={onUpload}
              disabled={uploadedFiles.length >= 5}
            >
              <Upload className="me-2" />
              Upload
            </Button>
          )}
          <Button
            type="submit"
            variant="primary"
            size="lg"
          >
            <Stars size={16} className="me-2" />
            {submitButtonText}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default ChatContainer;
